import { useContext } from "react";
import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { useI18n } from "i18n";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { Errors, trackError } from "modules/monitoring";
import { IEvent } from "modules/event/types";
import { useAppDispatch } from "store/hooks";
import { addEventDetails } from "modules/event/eventSlice";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import { addDialogNotification } from "modules/dialogNotification/redux/dialogNotificationSlice";
import { DialogType } from "modules/dialogNotification/constants";
import { selectCompany } from "modules/company/redux/selectors";
import request, { ValidationError } from "./request";
import EventValidationErrorPopup from "../../../eventForm/validationError/EventValidationErrorPopup";

const useRestartEvent = (eventId?: string) => {
  const { track } = useContext(TRACKING_CONTEXT);
  const { company, companyPlan } = useSelector(selectCompany);
  const dispatch = useAppDispatch();
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const { t } = useI18n(["event", "server", "common"]);

  const { mutate, status } = useMutation({
    mutationFn: () => {
      if (!eventId) {
        throw new Error("Missing event ID");
      }
      return request(eventId);
    },

    onSuccess: (event: IEvent) => {
      addSuccessNotification({
        message: t("restart.event.success"),
        position: "tc",
      });

      track(Events.RESTART_EVENT, {
        event: event.id,
      });

      if (company) {
        dispatch(addEventDetails({ ...event, company }));
      }
    },

    onError: (error: ValidationError, _) => {
      trackError(error, {
        label: Errors.RESTART_EVENT,
        eventId,
      });

      if (error.message === "server:events.concurrent.limit.exceeded") {
        dispatch(
          addDialogNotification({
            title: t("common:error"),
            content: (
              <EventValidationErrorPopup
                data={error.data}
                message={t("server:events.concurrent.limit.exceeded", {
                  planName: companyPlan?.name,
                  concurrent: companyPlan?.settings.event.concurrent,
                })}
              />
            ),
            hideCloseButton: true,
            hideDismissButton: true,
            confirmText: t("button.ok"),
            onConfirm: () => {},
            type: DialogType.ERROR_MESSAGE,
          }),
        );
      } else {
        addErrorNotification({
          message: t("restart.event.error"),
          position: "tc",
        });
      }
    },
  });

  return { restartEvent: mutate, status };
};

export default useRestartEvent;
